import { type Editor } from '@tiptap/react';
import { List, ListOrdered } from 'lucide-react';
import { type MouseEvent, type PointerEvent } from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useEditorUpdate } from '@/hooks/use-editor-update';
import { isMarkActive } from '@/utils/helpers/tiptap-helpers';

interface MenuBarProps {
  editor: Editor | null;
}

export const MenuBar = ({ editor }: MenuBarProps) => {
  useEditorUpdate(editor);

  if (!editor) {
    return null;
  }

  const activeValues: string[] = [];

  if (isMarkActive(editor, 'bold')) {
    activeValues.push('bold');
  }

  if (isMarkActive(editor, 'italic')) {
    activeValues.push('italic');
  }

  if (isMarkActive(editor, 'underline')) {
    activeValues.push('underline');
  }

  if (isMarkActive(editor, 'strike')) {
    activeValues.push('strike');
  }

  if (editor.isActive('bulletList')) {
    activeValues.push('bulletList');
  }

  if (editor.isActive('orderedList')) {
    activeValues.push('orderedList');
  }

  const preventFocusSteal = (e: PointerEvent | MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="flex items-center flex-wrap gap-1 border-b border-border py-1.5">
      <ToggleGroup type="multiple" value={activeValues} className="justify-start gap-1">
        <ToggleGroupItem
          value="bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          onMouseDown={preventFocusSteal}
          className="size-8 font-bold"
        >
          B
        </ToggleGroupItem>
        <ToggleGroupItem
          value="italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          onMouseDown={preventFocusSteal}
          className="size-8"
        >
          /
        </ToggleGroupItem>
        <ToggleGroupItem
          value="underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          onMouseDown={preventFocusSteal}
          className="size-8 underline underline-offset-4"
        >
          U
        </ToggleGroupItem>
        <ToggleGroupItem
          value="strike"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          onMouseDown={preventFocusSteal}
          className="size-8 line-through"
        >
          S
        </ToggleGroupItem>
        <div className="w-px h-5 bg-border mx-1" />
        <ToggleGroupItem
          value="bulletList"
          onClick={() => editor.chain().toggleBulletList().focus().run()}
          onMouseDown={preventFocusSteal}
          className="size-8"
        >
          <List size={18} />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="orderedList"
          onClick={() => editor.chain().toggleOrderedList().focus().run()}
          onMouseDown={preventFocusSteal}
          className="size-8"
        >
          <ListOrdered size={18} />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};
